import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { FormProvider, RHFAutoComplete } from '../../../components/hook-form';
import axios from '../../../utils/axios';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { BotaoAuth, CampoAuth, EntradaAuth, IconeEmail, IconePessoa } from '../CamposAuth';

/* ============================================================================
   Formulario de criar conta - visual novo (shadcn + Tailwind).

   NADA DO ENVIO FOI TOCADO: mesmo POST em `/login/novo`, mesmos campos
   enviados (nome, email, usuarioRegionais), mesmo redirecionamento para
   PATH_DASHBOARD.general.usersNovo, mesma ordem de leitura do erro.

   O `RHFAutoComplete` (campo "Regionais", paginado, ligado a API) continua
   sendo usado como esta - ele e um componente proprio fora deste lote de
   migracao e ainda tem MUI por dentro. Ele le o form pelo `useFormContext`,
   por isso o `FormProvider` (wrapper fino sobre o FormProvider do
   react-hook-form, sem nada de MUI) continua envolvendo o formulario.

   DEFEITOS CORRIGIDOS:

   1. `const { register } = useAuth()` trazia a funcao de registro do
      contexto de autenticacao, mas essa variavel NUNCA era chamada em
      lugar nenhum do arquivo - o cadastro sempre foi feito via
      `axios.post('/login/novo', ...)| direto. Pior: o nome `register`
      colidia com o `register` do react-hook-form (usado agora para ligar
      os campos nome/email), que so nao dava erro porque a versao antiga
      usava RHFTextField (baseado em Controller, nao em `register`). Eram
      dois imports mortos (`useAuth`, `useIsMountedRef`) escondendo essa
      colisao de nomes. Removidos.

   2. `if (error.errors || error.message)` sem optional chaining: quando a
      chamada falhava SEM resposta do servidor (rede fora do ar, timeout),
      `err?.response?.data` vinha `undefined` e essa linha explodia com
      TypeError dentro do proprio catch - a mensagem de erro de rede nunca
      chegava a aparecer, so um erro nao tratado no console. Agora usa
      `error?.errors` / `error?.message`, igual ao LoginForm.

   3. O botao usava `loading={isSubmitting}` - prop que o Button do MUI v9
      nao tem (era do LoadingButton do v5), entao nunca indicava
      carregando e dava para clicar duas vezes. Agora usa `BotaoAuth`, que
      desabilita e troca o rotulo enquanto envia.
   ============================================================================ */

export default function RegisterForm() {
  const { enqueueSnackbar } = useSnackbar();
  const { push } = useRouter();

  const RegisterSchema = Yup.object().shape({
    nome: Yup.string().required('Nome é obrigatorio'),
    email: Yup.string().email('E-mail é invalido').required('E-mail é obrigatorio'),
    // senha: Yup.string().required('Senha é obrigatorio'),
  });

  const methods = useForm({
    resolver: yupResolver(RegisterSchema),
    defaultValues: {
      nome: '',
      email: '',
      // senha: '',
    },
  });

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      const response = await axios.post(`/login/novo`, {
        nome: data.nome,
        email: data.email,
        usuarioRegionais: data.usuarioRegionais,
      });

      if (response.status === 200 || response.status === 201) {
        enqueueSnackbar(`Salvo com sucesso`, { variant: 'success', style: { whiteSpace: 'pre-line' } });
        reset();
      }

      // `usersNovo`, e nao `users`: a tela antiga foi removida hoje e este push
      // levaria a um 404 logo depois de criar a conta.
      push(PATH_DASHBOARD.general.usersNovo);
    } catch (err) {
      const error = err?.response?.data;
      console.error(error);

      if (error?.errors || error?.message) {
        if (error?.errors && error.errors.length !== 0)
          enqueueSnackbar(error.errors[0].message, { variant: 'error', style: { whiteSpace: 'pre-line' } });
        else if (error?.message)
          enqueueSnackbar(error.message, { variant: 'error', style: { whiteSpace: 'pre-line' } });
        else
          enqueueSnackbar(`Ops ocorreu um erro no sistema.`, { variant: 'error', style: { whiteSpace: 'pre-line' } });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-3.5">
        <CampoAuth id="register-nome" rotulo="Nome" erro={errors.nome?.message}>
          <EntradaAuth
            id="register-nome"
            autoComplete="name"
            icone={<IconePessoa />}
            erro={errors.nome}
            placeholder="Seu nome completo"
            {...register('nome')}
          />
        </CampoAuth>

        <CampoAuth id="register-email" rotulo="E-mail" erro={errors.email?.message}>
          <EntradaAuth
            id="register-email"
            type="email"
            autoComplete="email"
            icone={<IconeEmail />}
            erro={errors.email}
            placeholder="seu.email@empresa.com.br"
            {...register('email')}
          />
        </CampoAuth>

        {/* A busca aponta para `/regionais/publico`, e nao `/regionais`.
            Esta tela roda DESLOGADA, e `/regionais/paginado` exige token desde
            14/08 - o campo respondia 401 e ficava preso em "Buscando..." (o que
            travava, alem disso, por um defeito do proprio componente; ver
            components/hook-form/RHFAutoComplete.jsx). A rota publica devolve so
            codigo+nome das ativas, com 2+ caracteres de busca. */}
        <RHFAutoComplete
          label="Regionais"
          id="usuarioRegionais"
          name="usuarioRegionais"
          isPaginado
          multiple
          codigoData={'codigo'}
          labelData={'nome'}
          campoPesquisaPaginado="nome"
          urlAPI={`/regionais/publico`}
          objectValueSearchAPI={''}
        />

        {/* Campo de senha comentado desde a versao MUI (cadastro sem senha,
           definida depois pelo backend) - comportamento preservado, so
           mantido comentado. */}

        <BotaoAuth carregando={isSubmitting} rotuloCarregando="Enviando...">
          Criar conta
        </BotaoAuth>
      </div>
    </FormProvider>
  );
}
